import React, { FC } from "react";

import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { Bold } from "@UI/Components/Bold/Bold";
import { ListItem } from "@UI/Components/List/List";
import { Text } from "@UI/Components/Text/Text";
import { Box } from "@UI/Components/Box/Box";
import { Rate } from "@Shared/Components/Rate/Rate";
import { OrderDescriptionColorScheme } from "@modules/Purchasing/Components/OrderDescription/types";
import { getDarkBorderColor } from "../../../Purchasing/Components/OrderDescription/config";
import messages from "./messages";
import { useServiceOffer } from "@modules/ServiceAssignment/hooks/useServiceOffer";
import { ID } from "@types";

interface Props {
  serviceOfferId: ID;
  colorScheme?: OrderDescriptionColorScheme;
  preview?: boolean;
  includeFees?: boolean;
  includeAdvancePayment?: boolean;
}

const TOTAL_LABEL_FOR_BILLING_UNIT = {
  hour: messages.totalByHour,
  day: messages.totalByDay,
  week: messages.totalByWeek,
};

// The includeFees and includeAdvancePayment props are used for the serviceOffer Modal.
// In that step(When the SP has made an offer, we don't want to show the fees or advance payment yet)
export const TotalServiceOffer: FC<Props> = ({
  serviceOfferId,
  colorScheme,
  preview,
  includeFees,
  includeAdvancePayment,
}) => {
  const borderColor = getDarkBorderColor(colorScheme);

  const { serviceOffer, totalExpenses, totalExpensesFees } =
    useServiceOffer(serviceOfferId);

  const { rateCents, rateCurrency, billingUnit, fees, prepaidCents } =
    serviceOffer;

  const serviceFeeCents = fees?.[0]?.amountCents ?? 0;
  const totalExpensesPrice = totalExpenses + (totalExpensesFees ?? 0);

  const totalBasePrice = rateCents;
  const totalPriceWithFees = rateCents + serviceFeeCents;

  const totalExpensesWithFees = includeFees
    ? totalExpensesPrice
    : totalExpenses;
  const totalRate = includeFees ? totalPriceWithFees : totalBasePrice;
  const totalFinalAmount =
    totalRate +
    totalExpensesWithFees -
    (includeAdvancePayment ? prepaidCents : 0);

  return (
    <ListItem
      display="flex"
      justifyContent="space-between"
      borderTopColor={borderColor}
      borderTopWidth="1px"
      pt="5"
    >
      <Bold fontSize="lg">
        {billingUnit !== "flat" && preview ? (
          <FormattedMessage {...TOTAL_LABEL_FOR_BILLING_UNIT[billingUnit]} />
        ) : (
          <FormattedMessage {...messages.total} />
        )}
      </Bold>
      <Box textAlign="right">
        {billingUnit !== "flat" && preview ? (
          <>
            <Box>
              <Bold fontSize={{ base: "20px", lg: "xl" }}>
                <Rate
                  cents={totalRate}
                  currency={rateCurrency}
                  unit={billingUnit}
                />
              </Bold>
            </Box>

            {totalExpenses + (totalExpensesFees ?? 0) > 0 && (
              <>
                <Text as="span" fontSize="md">
                  (+{" "}
                  <Rate
                    cents={totalExpensesWithFees}
                    currency={rateCurrency}
                    unit="flat"
                  />
                  )
                </Text>
              </>
            )}
          </>
        ) : (
          <Bold fontSize={{ base: "20px", lg: "xl" }}>
            <Rate
              cents={totalFinalAmount}
              currency={rateCurrency}
              unit="flat"
            />
          </Bold>
        )}
      </Box>
    </ListItem>
  );
};
